import _ from 'lodash';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box } from 'reflexbox';

import { AddIcon } from '@treams/treams-icons';
import * as graphql from '@treams/treams-network';
import { Button, FlexGap, Heading3, Label, tokens } from '@treams/treams-ui';

import WorkspacePlanCard from './components/WorkspacePlanCard';
import { WorkspaceAdvanced } from './types';

export default function WorkspacePlan(props: { workspace: WorkspaceAdvanced }) {
	const { t } = useTranslation();

	const [create] = graphql.useCreateWorkspacePlanMutation({
		update(cache, { data }) {
			const newPlan = data?.createWorkspacePlan.plan;

			if (newPlan) {
				cache.modify({
					id: cache.identify(props.workspace),
					fields: {
						plans(existingPlans = [], { toReference }) {
							return [...existingPlans, toReference(newPlan)];
						},
					},
				});
			}
		},
	});
	const [openID, setOpenID] = useState<string | null>(null);

	const plans = _.sortBy(props.workspace.plans, (plan) => {
		return plan.endDate && new Date(plan.endDate).getTime();
	}).reverse();

	const addPlanOfType = useCallback(
		async (type: graphql.WorkspacePlanType) => {
			await create({
				variables: {
					input: {
						workspaceID: props.workspace.id,
						type,
					},
				},
			});
		},
		[create, props.workspace.id],
	);

	return (
		<Box flexGrow={1} flexBasis={0}>
			<Heading3 mb={2}>{t('settings.workspaces.edit.plans.type.title')}</Heading3>
			{plans.map((plan) => (
				<WorkspacePlanCard
					key={plan.id}
					plan={plan}
					workspace={props.workspace}
					isCollapsed={plan.id !== openID}
					toggleCollapse={() => {
						if (openID === plan.id) {
							setOpenID(null);
						} else {
							setOpenID(plan.id);
						}
					}}
				/>
			))}
			<Box mt={`${tokens.gap.element}px`}>
				<Label>{t('settings.workspaces.startTrial')}</Label>
			</Box>
			<FlexGap mt={`${tokens.gap.inline}px`} gap={tokens.gap.inline} flexWrap="wrap">
				{Object.keys(graphql.WorkspacePlanType).map((type) => (
					<Button
						key={type}
						icon={<AddIcon />}
						type="secondary"
						small
						onClick={addPlanOfType.bind(null, graphql.WorkspacePlanType[type])}
						title={t(`workspaces.plans.type.${_.camelCase(type)}`)}
					/>
				))}
			</FlexGap>
		</Box>
	);
}
